import { useRef } from 'react';

/**
 * Single-select segmented (pill) control — e.g. Theme / Density pickers.
 *
 * Public interface: `options` + `value` + `onChange`. Hides the radiogroup ARIA
 * semantics (role="radio", aria-checked), roving-tabindex keyboard navigation
 * (Arrow keys move selection), and the active-pill styling.
 *
 * Sibling to Tabs.jsx (underline page-tabs) — intentionally separate so the
 * brand-accented pill styling does not clash with the RCD `.segmented-control`.
 *
 * Props:
 *   options   — Array<{ value: string, label: node }>
 *   value     — currently selected value
 *   onChange  — (value: string) => void
 *   ariaLabel — accessible label for the group
 *   className — extra class(es) on the group container
 */
export default function SegmentedControl({
  options = [],
  value,
  onChange,
  ariaLabel,
  className = '',
  ...props
}) {
  const refs = useRef([]);
  const selectedIndex = options.findIndex((o) => o.value === value);

  const moveTo = (i) => {
    if (options.length === 0) return;
    const next = (i + options.length) % options.length;
    refs.current[next]?.focus();
    onChange?.(options[next].value);
  };

  const handleKeyDown = (e, i) => {
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      moveTo(i + 1);
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      moveTo(i - 1);
    }
  };

  return (
    <div
      className={['segmented', className].filter(Boolean).join(' ')}
      role="radiogroup"
      aria-label={ariaLabel}
      {...props}
    >
      {options.map((opt, i) => {
        const selected = opt.value === value;
        const focusable = selected || (selectedIndex === -1 && i === 0);
        return (
          <button
            key={opt.value}
            ref={(el) => { refs.current[i] = el; }}
            type="button"
            role="radio"
            aria-checked={selected}
            tabIndex={focusable ? 0 : -1}
            className={['segmented-item', selected ? 'segmented-item--active' : '']
              .filter(Boolean)
              .join(' ')}
            onClick={() => onChange?.(opt.value)}
            onKeyDown={(e) => handleKeyDown(e, i)}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
